import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Eye,
  Clock,
  Users,
  BarChart3,
  Play,
  Pause,
  CheckCircle,
  BookOpen,
  Search,
  Settings,
  UserCheck,
  Archive,
  X,
  FileText,
} from "lucide-react";
import quizService, { QuizFormData } from "../../services/quizService";
import { Quiz } from "../../types/quiz";
import { courseService, UnifiedCourse } from "../../services/courseService";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

const QuizManagement: React.FC = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [courses, setCourses] = useState<UnifiedCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [filters, setFilters] = useState({
    courseId: "",
    status: "" as "published" | "draft" | "archived" | "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [formData, setFormData] = useState<QuizFormData>(
    quizService.getDefaultQuizFormData()
  );

  // Draft quizzes state
  const [draftQuizzes, setDraftQuizzes] = useState<any[]>([]);

  useEffect(() => {
    loadQuizzes();
  }, [filters.courseId, filters.status, pagination?.page]);

  // Load courses only once when component mounts (for filter dropdown)
  useEffect(() => {
    loadCourses();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const response = await quizService.getTeacherQuizzes({
        courseId: filters.courseId || undefined,
        status: filters.status || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });

      // The API returns { quizzes, total, page, totalPages }
      const quizData = response.quizzes || [];

      // Enrich quiz data with proper question counts
      const enrichedQuizzes = quizData.map((quiz: Quiz) => ({
        ...quiz,
        questionCount:
          quiz.questionCount || (quiz.questions ? quiz.questions.length : 0),
      }));

      setQuizzes(enrichedQuizzes);
      setPagination({
        page: response.page || 1,
        limit: pagination.limit,
        total: response.total || 0,
        pages: response.totalPages || 0,
        hasNext: response.page < response.totalPages,
        hasPrev: response.page > 1,
      });
    } catch (error) {
      console.error("Error loading quizzes:", error);
      toast.error("Failed to load quizzes");
      setQuizzes([]); // Ensure quizzes is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await courseService.getAllCoursesForQuiz();
      setCourses(Array.isArray(response.data) ? response.data : []);
      // Load draft quizzes after courses are loaded
      setTimeout(() => loadDraftQuizzes(), 100);
    } catch (error) {
      console.error("Error loading courses:", error);
      toast.error("Failed to load courses");
      setCourses([]); // Ensure courses is always an array
    }
  };

  const handleCreateQuiz = () => {
    setFormData(quizService.getDefaultQuizFormData());
    setEditingQuiz(null);
    setShowCreateModal(true);
  };

  const handleSubmitQuiz = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingQuiz) {
        await quizService.updateQuiz(editingQuiz._id!, formData);
        toast.success("Quiz updated successfully");
        setShowCreateModal(false);
        loadQuizzes();
      } else {
        const response = await quizService.createQuiz(formData);
        toast.success("Quiz created successfully");
        setShowCreateModal(false);

        // Redirect to QuizCreator to add questions
        navigate(`/elearning/teacher/quizzes/${response._id}/edit`);
      }
    } catch (error: any) {
      console.error("Error saving quiz:", error);

      // Handle specific error messages from the service layer
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        // Fallback for other error types
        const errorData = error.response?.data;
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          // Display detailed question validation errors
          const errorMessages = errorData.errors.map(
            (err: any) =>
              `Question ${err.questionIndex}: ${err.errors.join(", ")}`
          );
          toast.error(`Validation failed:\n${errorMessages.join("\n")}`);
        } else {
          toast.error(errorData?.message || "Failed to save quiz");
        }
      }
    }
  };

  const handleDeleteQuiz = async (quiz: Quiz) => {
    if (!confirm(`Are you sure you want to delete "${quiz.title}"?`)) {
      return;
    }

    try {
      await quizService.deleteQuiz(quiz._id!);
      toast.success("Quiz deleted successfully");
      loadQuizzes();
    } catch (error: any) {
      console.error("Error deleting quiz:", error);
      toast.error(error.response?.data?.message || "Failed to delete quiz");
    }
  };

  const handleTogglePublication = async (quiz: Quiz) => {
    try {
      await quizService.toggleQuizPublication(quiz._id!, !quiz.isPublished);
      toast.success(
        `Quiz ${!quiz.isPublished ? "published" : "unpublished"} successfully`
      );
      loadQuizzes();
    } catch (error: any) {
      console.error("Error toggling publication:", error);
      toast.error(error.response?.data?.message || "Failed to update quiz");
    }
  };

  const handleArchiveQuiz = async (quiz: Quiz) => {
    if (!confirm(`Are you sure you want to archive "${quiz.title}"?`)) {
      return;
    }

    try {
      await quizService.archiveQuiz(quiz._id!);
      toast.success("Quiz archived successfully");
      loadQuizzes();
    } catch (error: any) {
      console.error("Error archiving quiz:", error);
      toast.error(error.response?.data?.message || "Failed to archive quiz");
    }
  };

  const handleUnarchiveQuiz = async (quiz: Quiz) => {
    if (!confirm(`Are you sure you want to unarchive "${quiz.title}"?`)) {
      return;
    }

    try {
      await quizService.unarchiveQuiz(quiz._id!);
      toast.success("Quiz unarchived successfully");
      loadQuizzes();
    } catch (error: any) {
      console.error("Error unarchiving quiz:", error);
      toast.error(error.response?.data?.message || "Failed to unarchive quiz");
    }
  };

  // Draft quiz functions
  const loadDraftQuizzes = () => {
    const drafts: any[] = [];
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

    // Check all possible draft keys in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("quiz_draft_")) {
        try {
          const storedData = JSON.parse(localStorage.getItem(key) || "{}");
          // Check if data is not expired
          if (Date.now() - storedData.timestamp < sevenDaysInMs) {
            drafts.push({
              ...storedData,
              storageKey: key,
              courseName:
                courses.find((c) => c._id === storedData.courseId)?.title ||
                "Unknown Course",
            });
          }
        } catch (error) {
          console.error("Error parsing draft quiz data:", error);
        }
      }
    }

    setDraftQuizzes(drafts);
  };

  const handleContinueDraft = (draft: any) => {
    // Store the draft data temporarily for the QuizCreator to pick up
    sessionStorage.setItem("quiz_draft_continue", JSON.stringify(draft));
    // Navigate to quiz creator with the course ID
    navigate(`/elearning/teacher/quizzes/create?courseId=${draft.courseId}`);
  };

  const handleDeleteDraft = (storageKey: string) => {
    if (confirm("Are you sure you want to delete this draft quiz?")) {
      localStorage.removeItem(storageKey);
      loadDraftQuizzes();
      toast.success("Draft quiz deleted");
    }
  };

  const activeQuizzes = Array.isArray(quizzes)
    ? quizzes.filter((quiz) => quiz.status !== "archived")
    : [];
  const archivedQuizzes = Array.isArray(quizzes)
    ? quizzes.filter((quiz) => quiz.status === "archived")
    : [];

  const filteredQuizzes = activeQuizzes.filter(
    (quiz) =>
      quiz.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      (quiz.description &&
        quiz.description.toLowerCase().includes(filters.search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Quiz Management
            </h1>
            <p className="text-slate-600 font-medium mt-1">
              Create and manage quizzes for your courses
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {archivedQuizzes.length > 0 && (
              <button
                onClick={() => setShowArchivedModal(true)}
                className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Archive size={20} className="text-slate-500" />
                <span className="text-slate-700">
                  Archived Quizzes ({archivedQuizzes.length})
                </span>
              </button>
            )}
            <button
              onClick={() => navigate("/elearning/teacher/quizzes/create")}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Plus size={20} className="text-emerald-500" />
              <span className="text-slate-700">Create Quiz with Questions</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search quizzes..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-12 pr-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Course
            </label>
            <select
              value={filters.courseId}
              onChange={(e) =>
                setFilters({ ...filters, courseId: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            >
              <option value="">All Courses</option>
              {Array.isArray(courses) &&
                courses.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.title}({course.level})
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value as
                    | "published"
                    | "draft"
                    | "archived"
                    | "",
                })
              }
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ courseId: "", status: "", search: "" });
                setPagination({ ...pagination, page: 1 });
              }}
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <span className="text-slate-700">Clear Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Draft Quiz Cards */}
      {draftQuizzes.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-2 shadow-lg">
              <FileText className="text-white" size={20} />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Draft Quizzes ({draftQuizzes.length})
            </h3>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {draftQuizzes.map((draft, index) => (
              <div
                key={draft.storageKey || `draft-${index}`}
                className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 text-lg mb-1 line-clamp-2">
                      {draft.quiz?.title || "Untitled Quiz"}
                    </h4>
                    <p className="text-sm text-slate-600 font-medium">
                      {draft.courseName || "No course selected"}
                    </p>
                  </div>
                  <div className="bg-amber-100 rounded-lg p-2">
                    <Clock className="text-amber-600" size={16} />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                  <span>
                    Last modified:{" "}
                    {new Date(draft.timestamp).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleContinueDraft(draft)}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Play className="text-white" size={16} />
                    <span>Continue</span>
                  </button>
                  <button
                    onClick={() => handleDeleteDraft(draft.storageKey)}
                    className="px-4 py-2 bg-white border-2 border-red-200 text-red-600 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quiz List */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500 mx-auto"></div>
            <p className="mt-4 text-slate-600 font-medium">
              Loading quizzes...
            </p>
          </div>
        ) : filteredQuizzes.length === 0 ? (
          <div className="p-12 text-center">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <BookOpen className="text-white" size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              No quizzes found
            </h3>
            <p className="mt-1 text-slate-600 font-medium">
              Get started by creating your first quiz.
            </p>
            <button
              onClick={handleCreateQuiz}
              className="mt-4 bg-white border-2 border-slate-200 px-6 py-3 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              <Plus size={20} className="text-emerald-500" />
              <span className="text-slate-700">Create Quiz</span>
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredQuizzes.map((quiz) => (
              <div
                key={quiz._id}
                className="p-6 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all duration-300"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-900">
                        {quiz.title}
                      </h3>
                      <span
                        className={`px-3 py-1.5 text-xs font-bold rounded-xl shadow-sm ${
                          quiz.isPublished
                            ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                            : "bg-gradient-to-br from-amber-300 to-yellow-400 text-white"
                        }`}
                      >
                        {quiz.isPublished ? "Published" : "Draft"}
                      </span>
                    </div>

                    {quiz.description && (
                      <p className="text-slate-600 font-medium mb-3">
                        {quiz.description}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-5 text-sm text-slate-600 font-medium">
                      <div className="flex items-center gap-1.5">
                        <BookOpen size={16} className="text-blue-500" />
                        <span>
                          {(quiz as any).courseId?.title ||
                            (quiz as any).courseId?.name ||
                            `Course ID: ${(quiz as any).courseId}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users size={16} className="text-purple-500" />
                        <span>{quiz.questionCount || 0} questions</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock size={16} className="text-amber-500" />
                        <span>
                          {quiz.timeLimit > 0
                            ? `${quiz.timeLimit} min`
                            : "No time limit"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle size={16} className="text-emerald-500" />
                        <span>{quiz.passingScore}% to pass</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    <button
                      onClick={() => handleTogglePublication(quiz)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title={quiz.isPublished ? "Unpublish" : "Publish"}
                    >
                      {quiz.isPublished ? (
                        <Pause size={18} className="text-amber-500" />
                      ) : (
                        <Play size={18} className="text-emerald-500" />
                      )}
                    </button>

                    <button
                      onClick={() =>
                        navigate(`/elearning/quizzes/${quiz._id}/analytics`)
                      }
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="View Analytics"
                    >
                      <BarChart3 size={18} className="text-blue-500" />
                    </button>

                    {quiz.pendingGradingCount &&
                      quiz.pendingGradingCount > 0 && (
                        <button
                          onClick={() =>
                            navigate(`/elearning/quizzes/${quiz._id}/grading`)
                          }
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                          title={`Grade ${
                            quiz.pendingGradingCount
                          } pending submission${
                            quiz.pendingGradingCount !== 1 ? "s" : ""
                          }`}
                        >
                          <UserCheck size={18} className="text-orange-500" />
                        </button>
                      )}

                    <button
                      onClick={() =>
                        navigate(
                          `/elearning/teacher/quizzes/${quiz._id}/preview`
                        )
                      }
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Preview Quiz"
                    >
                      <Eye size={18} className="text-slate-500" />
                    </button>

                    <button
                      onClick={() =>
                        navigate(`/elearning/teacher/quizzes/${quiz._id}/edit`)
                      }
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Edit Questions"
                    >
                      <Settings size={18} className="text-purple-500" />
                    </button>

                    {quiz.status !== "archived" && (
                      <button
                        onClick={() => handleArchiveQuiz(quiz)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Archive Quiz"
                      >
                        <Archive size={18} className="text-slate-500" />
                      </button>
                    )}

                    <button
                      onClick={() => handleDeleteQuiz(quiz)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Delete Quiz"
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredQuizzes.length > 0 && (
        <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) => setPagination({ ...pagination, page })}
            pageSize={pagination.limit}
            onPageSizeChange={(size) => {
              setPagination({ ...pagination, limit: size, page: 1 });
            }}
            showPageSizeSelector={true}
            className="w-full"
          />
        </div>
      )}

      {/* Summary */}
      {filteredQuizzes.length > 0 && (
        <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-4">
          <p className="text-sm text-slate-700 font-semibold">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} quizzes
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                {editingQuiz ? "Edit Quiz" : "Create New Quiz"}
              </h2>
            </div>

            <form onSubmit={handleSubmitQuiz} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Quiz Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="Enter quiz title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                  placeholder="Enter quiz description"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Course *
                </label>
                <select
                  required
                  value={formData.courseId}
                  onChange={(e) =>
                    setFormData({ ...formData, courseId: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="">Select a course</option>
                  {Array.isArray(courses) &&
                    courses.map((course) => (
                      <option key={course._id} value={course._id}>
                        {course.title}({course.level})
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Time Limit (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.timeLimit || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeLimit:
                          e.target.value === ""
                            ? undefined
                            : parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    placeholder="0 for unlimited"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Attempts Allowed
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.attempts || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue =
                        value === "" ? undefined : parseInt(value);
                      setFormData({
                        ...formData,
                        attempts:
                          numValue !== undefined && !isNaN(numValue)
                            ? numValue
                            : undefined,
                      });
                    }}
                    placeholder="1"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Passing Score (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.passingScore || ""}
                  placeholder="60"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      passingScore:
                        e.target.value === ""
                          ? undefined
                          : parseInt(e.target.value) || 60,
                    })
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="shuffleQuestions"
                    checked={formData.shuffleQuestions}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shuffleQuestions: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-slate-300 rounded"
                  />
                  <label
                    htmlFor="shuffleQuestions"
                    className="ml-2 text-sm font-medium text-slate-700"
                  >
                    Shuffle Questions
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showResults"
                    checked={formData.showResults}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showResults: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-slate-300 rounded"
                  />
                  <label
                    htmlFor="showResults"
                    className="ml-2 text-sm font-medium text-slate-700"
                  >
                    Show Results to Students
                  </label>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-5 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <span className="text-slate-700">Cancel</span>
                </button>
                <button
                  type="submit"
                  className="flex-1 px-5 py-2.5 bg-gradient-to-br from-emerald-300 to-emerald-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  {editingQuiz ? "Update Quiz" : "Create Quiz"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archived Quizzes Modal */}
      {showArchivedModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative my-8 w-full max-w-6xl bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                Archived Quizzes
              </h3>
              <button
                onClick={() => setShowArchivedModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>

            <div className="p-6">
              {archivedQuizzes.length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
                    <Archive className="text-white" size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    No archived quizzes
                  </h3>
                  <p className="mt-1 text-slate-600 font-medium">
                    Archived quizzes will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {archivedQuizzes.map((quiz) => (
                    <div
                      key={quiz._id}
                      className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-bold text-slate-900">
                              {quiz.title}
                            </h3>
                            <span className="px-3 py-1.5 text-xs font-bold rounded-xl shadow-sm bg-gradient-to-br from-slate-300 to-slate-400 text-white">
                              Archived
                            </span>
                          </div>

                          {quiz.description && (
                            <p className="text-slate-600 font-medium mb-3">
                              {quiz.description}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-5 text-sm text-slate-600 font-medium">
                            <div className="flex items-center gap-1.5">
                              <BookOpen size={16} className="text-blue-500" />
                              <span>
                                {(quiz as any).courseId?.title ||
                                  (quiz as any).courseId?.name ||
                                  "No Course Assigned"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Users size={16} className="text-purple-500" />
                              <span>{quiz.questionCount || 0} questions</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock size={16} className="text-amber-500" />
                              <span>
                                {quiz.timeLimit > 0
                                  ? `${quiz.timeLimit} min`
                                  : "No time limit"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <CheckCircle
                                size={16}
                                className="text-emerald-500"
                              />
                              <span>{quiz.passingScore}% to pass</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleUnarchiveQuiz(quiz)}
                          className="p-2 rounded-lg hover:bg-white transition-colors flex-shrink-0"
                          title="Unarchive Quiz"
                        >
                          <Archive size={18} className="text-emerald-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizManagement;
